"use client";

import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Icon from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdownMenu";
import { MIN_ITEMS_TO_SHOW_SEARCH_FILTER } from "@/components/Search/constants";
import { useSearchMessages } from "@/i18n/hooks/useSearchMessages";
import useTranslations from "@/i18n/useTranslations";
import { cn } from "@/lib/utils";
import { PARTNER_FILTER_CATEGORY } from "./constants";
import type { PartnerOption } from "./types";

const toHtmlId = (label: string) =>
  `filter-${PARTNER_FILTER_CATEGORY}-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`;

type PartnerFilterProps = {
  options: PartnerOption[];
  selected: string[];
};

const PartnerFilter = ({ options, selected }: PartnerFilterProps) => {
  const t = useTranslations("Common");
  const {
    getFilterCategoryName,
    getSearchFilterPlaceholder,
    getSearchFilterAriaLabel,
  } = useSearchMessages();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [optimisticSelected, setOptimisticSelected] = useState(selected);
  const selectedRef = useRef(selected);
  const selectedKey = selected.join("\0");

  useEffect(() => {
    selectedRef.current = selected;
    setOptimisticSelected(selected);
  }, [selectedKey, selected]);

  if (options.length === 0) return null;

  const selectedSet = new Set(optimisticSelected);

  const apply = (next: string[]) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("partners");
    next.forEach((partner) => params.append("partners", partner));
    const query = params.toString();
    router.push(query ? `${pathname}?${query}` : pathname, { scroll: false });
  };

  const toggle = (partner: string) => {
    const current = selectedRef.current;
    const next = current.includes(partner)
      ? current.filter((item) => item !== partner)
      : [...current, partner].sort((left, right) => left.localeCompare(right));
    selectedRef.current = next;
    setOptimisticSelected(next);
    apply(next);
  };

  const visibleOptions = [
    ...options.filter((option) => selectedSet.has(option.name)),
    ...options.filter((option) => !selectedSet.has(option.name)),
  ].filter((option) =>
    option.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const categoryName = getFilterCategoryName(PARTNER_FILTER_CATEGORY);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          id={`filter-${PARTNER_FILTER_CATEGORY}-trigger`}
          variant="primaryOutline"
          className={cn(
            "flex h-8 items-center gap-1 whitespace-nowrap border-2 px-3 py-1 text-sm font-normal",
            optimisticSelected.length > 0 && "font-semibold",
          )}
          aria-haspopup="menu"
        >
          <span className="m-0 p-0 text-sm hover:text-white">
            {`${categoryName}  ${optimisticSelected.length > 0 ? `(${optimisticSelected.length})` : ""}`}
          </span>
          <Icon
            type="keyboard-arrow-down-material"
            svgProps={{ "aria-hidden": true, width: 16, height: 16 }}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="z-50 mx-2 max-h-80 w-60 gap-2 overflow-y-auto p-0 scrollbar-thin"
      >
        {options.length > MIN_ITEMS_TO_SHOW_SEARCH_FILTER && (
          <div className="relative w-full">
            <input
              type="search"
              className="my-1 flex min-h-9 w-full border-0 border-b border-gray-300 bg-white text-xs md:my-0"
              placeholder={getSearchFilterPlaceholder(
                PARTNER_FILTER_CATEGORY,
                options.length,
              )}
              aria-label={getSearchFilterAriaLabel(PARTNER_FILTER_CATEGORY)}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                event.stopPropagation()
              }
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 transform text-gray-400">
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="hover:text-gray-600"
                  aria-label={t("clearSearch")}
                >
                  <Icon type="close" svgProps={{ "aria-hidden": true }} />
                </button>
              ) : (
                <Icon
                  type="manifying-glass"
                  svgProps={{ "aria-hidden": true }}
                />
              )}
            </div>
          </div>
        )}
        <div>
          {visibleOptions.map((partner) => {
            const isSelected = selectedSet.has(partner.name);
            const htmlId = toHtmlId(partner.name);
            return (
              <DropdownMenuItem
                key={partner.name}
                onSelect={(event) => {
                  event.preventDefault();
                  toggle(partner.name);
                }}
                className={cn(
                  "px-3 hover:cursor-pointer hover:bg-putty-200",
                  isSelected && "bg-putty-200",
                )}
              >
                <div className="flex items-center justify-start py-1.5">
                  <input
                    type="checkbox"
                    id={htmlId}
                    checked={isSelected}
                    readOnly
                    tabIndex={-1}
                    className="mr-2 h-4 w-4 min-w-4 cursor-pointer border-2 border-gray-700"
                  />
                  <label
                    htmlFor={htmlId}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      toggle(partner.name);
                    }}
                    className="mb-0 flex items-center text-xs leading-4 text-gray-700"
                  >
                    {`${partner.name} (${partner.count})`}
                  </label>
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PartnerFilter;
